import { ASC, DESC } from 'framework/list/baseListHelper';
import type { CollectionViewParams, CollectionViewParamsConfig } from './types';
import { restoreCollectionFilters, restoreCollectionSort } from './validation';

const getParamNames = <Filters>(config: CollectionViewParamsConfig<Filters>) => ({
  filter: config.filterParam ?? 'filter',
  sortBy: config.sortByParam ?? 'sortBy',
  sortOrder: config.sortOrderParam ?? 'sortOrder',
});

const normalizeSearch = (search: string) =>
  Array.from(new URLSearchParams(search).entries()).sort(([leftKey, leftValue], [rightKey, rightValue]) =>
    `${leftKey}=${leftValue}`.localeCompare(`${rightKey}=${rightValue}`),
  );

export const areCollectionViewSearchesEqual = (left: string, right: string) =>
  JSON.stringify(normalizeSearch(left)) === JSON.stringify(normalizeSearch(right));

const parseFilters = <Filters>(value: string | null, config: CollectionViewParamsConfig<Filters>): Filters => {
  const fallbackFilters = config.defaultFilters ?? ({} as Filters);
  if (!value) return fallbackFilters;

  try {
    const parsed = JSON.parse(value);
    return restoreCollectionFilters(parsed, config);
  } catch {
    return fallbackFilters;
  }
};

export const hasCollectionViewUrlParams = <Filters>(search: string, config: CollectionViewParamsConfig<Filters>) => {
  const names = getParamNames(config);
  const searchParams = new URLSearchParams(search);
  return [names.filter, names.sortBy, names.sortOrder].some(param => searchParams.has(param));
};

export const readCollectionViewUrlParams = <Filters>(
  search: string,
  config: CollectionViewParamsConfig<Filters>,
): CollectionViewParams<Filters> => {
  const names = getParamNames(config);
  const searchParams = new URLSearchParams(search);
  const sortColumn = searchParams.get(names.sortBy) || config.defaultSortBy;
  const sortOrder = searchParams.get(names.sortOrder)?.toUpperCase();
  const sortDirection = sortOrder === ASC || sortOrder === DESC ? sortOrder : config.defaultSortOrder;

  return {
    filters: parseFilters(searchParams.get(names.filter), config),
    sort: restoreCollectionSort({ columnId: sortColumn, direction: sortDirection }, config),
  };
};

export const buildCollectionViewSearch = <Filters>(
  currentSearch: string,
  params: CollectionViewParams<Filters>,
  config: CollectionViewParamsConfig<Filters>,
) => {
  const names = getParamNames(config);
  const searchParams = new URLSearchParams(currentSearch);
  [names.filter, names.sortBy, names.sortOrder].forEach(param => searchParams.delete(param));

  if (params.sort) {
    searchParams.set(names.sortBy, params.sort.columnId);
    searchParams.set(names.sortOrder, params.sort.direction);
  }

  if (params.filters && Object.keys(params.filters).length > 0) {
    searchParams.set(names.filter, JSON.stringify(params.filters));
  }

  const search = searchParams.toString();
  return search ? `?${search}` : '';
};
